import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Stack, Alert, CircularProgress, LinearProgress, Chip } from '@mui/material';
import {
  EasyCheckButton,
  EasyCheckTextField,
  EasyCheckDialog
} from '../../common/Controls';
import {
  Receipt as TemplateIcon,
  Help as HelpIcon,
  Person as BeneficiaryIcon,
  CalendarMonth as DateIcon,
  AttachMoney as AmountIcon,
  TextFields as WordsIcon,
  Straighten as SizeIcon,
  AccountBalance as PayeeIcon,
  Notes as RemarksIcon,
  CloudUpload as UploadIcon,
  AutoFixHigh as DetectIcon,
  ImageSearch as ImageIcon,
  Delete as DeleteIcon,
  ClearAll as ClearAllIcon,
  LibraryBooks as LibraryBooksIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import TemplateHelpModal from './TemplateHelpModal';
import TemplateImportDialog from './TemplateImportDialog';
import ChequeImagePreview, { FIELD_CONFIG } from './ChequeImagePreview';
import DpiInputDialog from './DpiInputDialog';
import { chequeTemplateService } from '../../../services/templates/chequeTemplateService';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import easyCheckApi from '../../../services/api/config';

const positionRules = {
  required: 'Required',
  max: { value: 30, message: 'Max 30' },
  min: { value: -30, message: 'Min -30' }
};

const dimensionRules = {
  required: 'Required',
  max: { value: 30, message: 'Max 30' },
  min: { value: 0.1, message: 'Min 0.1' }
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Extracted outside component body to prevent focus loss on re-renders
const SectionPanel = ({ children, icon, title, gradient = 'default' }) => {
  const gradients = {
    default: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
    alt: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
    accent: 'linear-gradient(135deg, #eef1ff 0%, #e4e9ff 100%)',
  };

  return (
    <Box
      sx={{
        background: gradients[gradient],
        borderRadius: 2.5,
        p: 1.5,
        position: 'relative',
        border: '1px solid',
        borderColor: 'rgba(37, 99, 235, 0.1)',
        boxShadow: `
          0 1px 3px rgba(37, 99, 235, 0.06),
          0 4px 12px rgba(37, 99, 235, 0.04),
          inset 0 1px 0 rgba(255, 255, 255, 0.9),
          inset 0 -1px 0 rgba(37, 99, 235, 0.04)
        `,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: `
            0 2px 6px rgba(37, 99, 235, 0.1),
            0 8px 20px rgba(37, 99, 235, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.95),
            inset 0 -1px 0 rgba(37, 99, 235, 0.05)
          `,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
                color: 'white',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: '#1e3a5f', fontSize: '0.8rem' }}
          >
            {title}
          </Typography>
        </Box>
      )}
      {children}
    </Box>
  );
};

const TemplateFormModal = ({ open, onClose, onSave, bank, template }) => {
  const [loading, setLoading] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [showDpiDialog, setShowDpiDialog] = useState(false);
  const [pendingUploadData, setPendingUploadData] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [detectMessage, setDetectMessage] = useState(null);
  const [placementField, setPlacementField] = useState(null);
  const [designerEnabled, setDesignerEnabled] = useState(false);
  const fileInputRef = useRef(null);

  const { data: companyPreferences } = useQuery({
    queryKey: ['companyPreferences'],
    queryFn: async () => {
      const result = await companyPreferencesService.getPreferences();
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const includeBeneficiary2 = companyPreferences?.includeBeneficiary2 ?? companyPreferences?.IncludeBeneficiary2 ?? false;
  const includeRemarks = companyPreferences?.includeRemarks ?? companyPreferences?.IncludeRemarks ?? false;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      templateHeight: '',
      templateWidth: '',
      beneficiaryLeft: '',
      beneficiaryTop: '',
      beneficiaryWidth: '',
      beneficiary2Left: '',
      beneficiary2Top: '',
      dateLeft: '',
      dateTop: '',
      dateWidth: '',
      amountLeft: '',
      amountTop: '',
      amountWidth: '',
      amountInWords1Left: '',
      amountInWords1Top: '',
      amountInWordsWidth: '',
      amountInWords2Left: '',
      amountInWords2Top: '',
      amount2Width: '',
      accountPayeeLeft: '',
      accountPayeeTop: '',
      remarksLeft: '',
      remarksTop: '',
      remarksWidth: ''
    }
  });

  const formValues = watch();

  useEffect(() => {
    if (template) {
      const getValue = (pascalCase, camelCase) => {
        const value = template[pascalCase] ?? template[camelCase];
        return value !== null && value !== undefined ? value : '';
      };

      const formData = {
        templateHeight: getValue('TemplateHeight', 'templateHeight'),
        templateWidth: getValue('TemplateWidth', 'templateWidth'),
        beneficiaryLeft: getValue('BeneficiaryLeft', 'beneficiaryLeft'),
        beneficiaryTop: getValue('BeneficiaryTop', 'beneficiaryTop'),
        beneficiaryWidth: getValue('BeneficiaryWidth', 'beneficiaryWidth'),
        beneficiary2Left: getValue('Beneficiary2Left', 'beneficiary2Left'),
        beneficiary2Top: getValue('Beneficiary2Top', 'beneficiary2Top'),
        dateLeft: getValue('DateLeft', 'dateLeft'),
        dateTop: getValue('DateTop', 'dateTop'),
        dateWidth: getValue('DateWidth', 'dateWidth'),
        amountLeft: getValue('AmountLeft', 'amountLeft'),
        amountTop: getValue('AmountTop', 'amountTop'),
        amountWidth: getValue('AmountWidth', 'amountWidth'),
        amountInWords1Left: getValue('AmountInWords1Left', 'amountInWords1Left'),
        amountInWords1Top: getValue('AmountInWords1Top', 'amountInWords1Top'),
        amountInWordsWidth: getValue('AmountInWordsWidth', 'amountInWordsWidth'),
        amountInWords2Left: getValue('AmountInWords2Left', 'amountInWords2Left'),
        amountInWords2Top: getValue('AmountInWords2Top', 'amountInWords2Top'),
        amount2Width: getValue('Amount2Width', 'amount2Width'),
        accountPayeeLeft: getValue('AccountPayeeLeft', 'accountPayeeLeft'),
        accountPayeeTop: getValue('AccountPayeeTop', 'accountPayeeTop'),
        remarksLeft: getValue('RemarksLeft', 'remarksLeft'),
        remarksTop: getValue('RemarksTop', 'remarksTop'),
        remarksWidth: getValue('RemarksWidth', 'remarksWidth')
      };
      reset(formData);

      // Load existing cheque image if template has one
      const imageFilename = template.ChequeImageFilename || template.chequeImageFilename;
      if (imageFilename) {
        setUploadedImage({ FileName: imageFilename });
        // Fetch image via Axios with blob response type
        easyCheckApi.get(`/cheque-textract/image/${encodeURIComponent(imageFilename)}`, {
          responseType: 'blob',
          headers: { 'Accept': '*/*' },
          timeout: 30000
        })
          .then(res => {
            if (res.data) {
              console.log('Image loaded, size:', res.data.size, 'type:', res.data.type);
              setImagePreviewUrl(URL.createObjectURL(res.data));
            }
          })
          .catch((err) => {
            console.error('Failed to load template image:', err?.response?.status, err?.message);
          });
      } else {
        setUploadedImage(null);
        setImagePreviewUrl(null);
      }
    } else {
      reset({
        templateHeight: '',
        templateWidth: '',
        beneficiaryLeft: '',
        beneficiaryTop: '',
        beneficiaryWidth: '',
        beneficiary2Left: '',
        beneficiary2Top: '',
        dateLeft: '',
        dateTop: '',
        dateWidth: '',
        amountLeft: '',
        amountTop: '',
        amountWidth: '',
        amountInWords1Left: '',
        amountInWords1Top: '',
        amountInWordsWidth: '',
        amountInWords2Left: '',
        amountInWords2Top: '',
        amount2Width: '',
        accountPayeeLeft: '',
        accountPayeeTop: '',
        remarksLeft: '',
        remarksTop: '',
        remarksWidth: ''
      });
      setUploadedImage(null);
      setImagePreviewUrl(null);
    }
    setDetectMessage(null);
  }, [template, reset]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/tiff'];
    if (!allowedTypes.includes(file.type)) {
      setDetectMessage({ type: 'error', text: 'Invalid file type. Please upload PNG, JPEG, or TIFF.' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setDetectMessage({ type: 'error', text: 'File too large. Maximum size is 10MB.' });
      return;
    }

    // Show preview immediately regardless of upload result
    const localUrl = URL.createObjectURL(file);
    setImagePreviewUrl(localUrl);

    setIsUploading(true);
    setDetectMessage(null);
    try {
      const result = await chequeTemplateService.uploadChequeImage(bank?.id, file);
      setUploadedImage(result);

      // Always show dimensions dialog for user to confirm/enter cheque size
      setPendingUploadData(result);
      setShowDpiDialog(true);
    } catch (error) {
      setDetectMessage({ type: 'error', text: `Upload failed: ${error.message || 'Unknown error'}` });
    } finally {
      setIsUploading(false);
    }
  }, [bank?.id, setValue]);

  const handleDpiConfirm = (widthCm, heightCm) => {
    setShowDpiDialog(false);
    if (pendingUploadData) {
      setUploadedImage({ ...pendingUploadData, PhysicalWidthCm: widthCm, PhysicalHeightCm: heightCm });
      setValue('templateWidth', widthCm);
      setValue('templateHeight', heightCm);
      setPendingUploadData(null);

      const px = pendingUploadData.PixelWidth || 0;
      const warnings = [];
      if (px > 0 && px < 500) {
        warnings.push(`This image is only ${px}px wide — too small for accurate auto-detection. Rescan at 150 DPI or higher if detection is poor.`);
      }
      if (widthCm > 0 && widthCm < 12) {
        warnings.push(`Template width ${widthCm}cm looks too small for a real cheque (usually 15–20cm) — printing will be misplaced. Enter the actual cheque size in cm.`);
      }
      setDetectMessage(
        warnings.length > 0
          ? { type: 'warning', text: warnings.join(' ') }
          : { type: 'success', text: 'Dimensions set. Click "Auto-detect Fields" to detect positions.' }
      );
      setDesignerEnabled(true);
    }
  };

  const handleDetectFields = async () => {
    if (!uploadedImage?.FileName) {
      setDetectMessage({ type: 'error', text: 'Please upload a cheque image first.' });
      return;
    }

    const tw = parseFloat(formValues.templateWidth);
    const th = parseFloat(formValues.templateHeight);
    if (isNaN(tw) || isNaN(th) || tw <= 0 || th <= 0) {
      setDetectMessage({ type: 'error', text: 'Template width and height are required for detection.' });
      return;
    }

    setIsDetecting(true);
    setDetectMessage(null);
    try {
      const result = await chequeTemplateService.detectFields(
        uploadedImage.FileName,
        tw,
        th
      );

      const fields = result.DetectedFields || result.detectedFields;
      if (fields) {
        // Use setValue to merge detected values without replacing the whole form
        const fieldMap = {
          BeneficiaryLeft: 'beneficiaryLeft',
          BeneficiaryTop: 'beneficiaryTop',
          BeneficiaryWidth: 'beneficiaryWidth',
          Beneficiary2Left: 'beneficiary2Left',
          Beneficiary2Top: 'beneficiary2Top',
          DateLeft: 'dateLeft',
          DateTop: 'dateTop',
          DateWidth: 'dateWidth',
          AmountLeft: 'amountLeft',
          AmountTop: 'amountTop',
          AmountWidth: 'amountWidth',
          AmountInWords1Left: 'amountInWords1Left',
          AmountInWords1Top: 'amountInWords1Top',
          AmountInWordsWidth: 'amountInWordsWidth',
          AmountInWords2Left: 'amountInWords2Left',
          AmountInWords2Top: 'amountInWords2Top',
          Amount2Width: 'amount2Width',
          AccountPayeeLeft: 'accountPayeeLeft',
          AccountPayeeTop: 'accountPayeeTop',
          RemarksLeft: 'remarksLeft',
          RemarksTop: 'remarksTop',
          RemarksWidth: 'remarksWidth'
        };

        let fieldsPopulated = 0;
        Object.entries(fieldMap).forEach(([pascal, camel]) => {
          const val = fields[pascal] ?? fields[camel];
          if (val !== null && val !== undefined && val !== 0) {
            setValue(camel, val);
            fieldsPopulated++;
          }
        });

        const confidence = result.Confidence || result.confidence || 0;
        setDetectMessage({
          type: fieldsPopulated > 0 ? 'success' : 'warning',
          text: fieldsPopulated > 0
            ? `Detected ${fieldsPopulated} field positions (confidence: ${confidence}%). Review and adjust as needed.`
            : 'No fields could be detected. Please enter positions manually.'
        });
      }
    } catch (error) {
      setDetectMessage({ type: 'error', text: `Detection failed: ${error.message || 'Unknown error'}` });
    } finally {
      setIsDetecting(false);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setImagePreviewUrl(null);
    setDetectMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const requiredFields = [
        'templateHeight', 'templateWidth', 'beneficiaryLeft', 'beneficiaryTop',
        'beneficiaryWidth',
        ...(includeBeneficiary2 ? ['beneficiary2Left', 'beneficiary2Top'] : []),
        'dateLeft', 'dateTop', 'amountLeft', 'amountTop',
        'amountInWords1Left', 'amountInWords1Top', 'amountInWordsWidth',
        'amountInWords2Left', 'amountInWords2Top', 'amount2Width',
        'accountPayeeLeft', 'accountPayeeTop',
        ...(includeRemarks ? ['remarksLeft', 'remarksTop'] : [])
      ];

      const missingFields = requiredFields.filter(field =>
        data[field] === null || data[field] === undefined || data[field] === ''
      );
      if (missingFields.length > 0) {
        setLoading(false);
        return;
      }

      // Convert string values to numbers
      const numericData = { ...data };
      Object.keys(numericData).forEach(key => {
        if (key !== 'bankId' && key !== 'chequeImageFilename' &&
            numericData[key] !== null && numericData[key] !== undefined && numericData[key] !== '') {
          numericData[key] = parseFloat(numericData[key]);
        }
      });

      // Zero out coordinates for disabled preference fields
      if (!includeBeneficiary2) {
        numericData.beneficiary2Left = 0;
        numericData.beneficiary2Top = 0;
      }
      if (!includeRemarks) {
        numericData.remarksLeft = 0;
        numericData.remarksTop = 0;
      }

      // Include image filename if uploaded
      const saveData = {
        ...numericData,
        bankId: bank.id,
        chequeImageFilename: uploadedImage?.FileName || null
      };

      await onSave(saveData);
      setLoading(false);
    } catch (error) {
      console.error('Error in template form submission:', error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setUploadedImage(null);
    setImagePreviewUrl(null);
    setDetectMessage(null);
    setActiveField(null);
    setPlacementField(null);
    setDesignerEnabled(false);
    onClose();
  };

  const handleLibraryImport = (libraryTemplate) => {
    const getValue = (key) => {
      const val = libraryTemplate[key];
      return val !== null && val !== undefined ? val : '';
    };
    setValue('templateHeight', getValue('templateHeight'), { shouldDirty: true });
    setValue('templateWidth', getValue('templateWidth'), { shouldDirty: true });
    setValue('beneficiaryLeft', getValue('beneficiaryLeft'), { shouldDirty: true });
    setValue('beneficiaryTop', getValue('beneficiaryTop'), { shouldDirty: true });
    setValue('beneficiaryWidth', getValue('beneficiaryWidth'), { shouldDirty: true });
    setValue('beneficiary2Left', getValue('beneficiary2Left'), { shouldDirty: true });
    setValue('beneficiary2Top', getValue('beneficiary2Top'), { shouldDirty: true });
    setValue('dateLeft', getValue('dateLeft'), { shouldDirty: true });
    setValue('dateTop', getValue('dateTop'), { shouldDirty: true });
    setValue('dateWidth', getValue('dateWidth'), { shouldDirty: true });
    setValue('amountLeft', getValue('amountLeft'), { shouldDirty: true });
    setValue('amountTop', getValue('amountTop'), { shouldDirty: true });
    setValue('amountWidth', getValue('amountWidth'), { shouldDirty: true });
    setValue('amountInWords1Left', getValue('amountInWords1Left'), { shouldDirty: true });
    setValue('amountInWords1Top', getValue('amountInWords1Top'), { shouldDirty: true });
    setValue('amountInWordsWidth', getValue('amountInWordsWidth'), { shouldDirty: true });
    setValue('amountInWords2Left', getValue('amountInWords2Left'), { shouldDirty: true });
    setValue('amountInWords2Top', getValue('amountInWords2Top'), { shouldDirty: true });
    setValue('amount2Width', getValue('amount2Width'), { shouldDirty: true });
    setValue('accountPayeeLeft', getValue('accountPayeeLeft'), { shouldDirty: true });
    setValue('accountPayeeTop', getValue('accountPayeeTop'), { shouldDirty: true });
    setValue('remarksLeft', getValue('remarksLeft'), { shouldDirty: true });
    setValue('remarksTop', getValue('remarksTop'), { shouldDirty: true });
    setValue('remarksWidth', getValue('remarksWidth'), { shouldDirty: true });
    setImportDialogOpen(false);
  };

  const handleFieldClick = (fieldKey) => {
    setActiveField(fieldKey === activeField ? null : fieldKey);
    setPlacementField(null);
  };

  const handleClearAllFields = () => {
    const positionFields = [
      'beneficiaryLeft', 'beneficiaryTop', 'beneficiaryWidth',
      'beneficiary2Left', 'beneficiary2Top',
      'dateLeft', 'dateTop', 'dateWidth',
      'amountLeft', 'amountTop', 'amountWidth',
      'amountInWords1Left', 'amountInWords1Top', 'amountInWordsWidth',
      'amountInWords2Left', 'amountInWords2Top', 'amount2Width',
      'accountPayeeLeft', 'accountPayeeTop',
      'remarksLeft', 'remarksTop', 'remarksWidth'
    ];
    positionFields.forEach(field => setValue(field, ''));
    setActiveField(null);
    setPlacementField(null);
  };

  const handleFieldMove = useCallback((fieldKey, newLeftCm, newTopCm) => {
    const config = FIELD_CONFIG[fieldKey];
    if (!config) return;
    setValue(config.fields.left, newLeftCm);
    setValue(config.fields.top, newTopCm);
  }, [setValue]);

  const handleFieldResize = useCallback((fieldKey, newWidthCm) => {
    const config = FIELD_CONFIG[fieldKey];
    if (!config?.fields.width) return;
    setValue(config.fields.width, newWidthCm);
  }, [setValue]);

  const handlePlaceField = useCallback((fieldKey, leftCm, topCm) => {
    const config = FIELD_CONFIG[fieldKey];
    if (!config) return;
    setValue(config.fields.left, leftCm);
    setValue(config.fields.top, topCm);
    setPlacementField(null);
  }, [setValue]);

  const renderField = (name, caption, rules = positionRules) => (
    <Box sx={{ flex: 1, minWidth: 60 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.25,
          fontWeight: 600,
          lineHeight: 1.2,
          color: '#475569',
          letterSpacing: '0.02em',
          fontSize: '0.68rem',
        }}
      >
        {caption}
      </Typography>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <EasyCheckTextField
            {...field}
            variant="compact"
            type="number"
            inputProps={{ step: "any" }}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
            }}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(4px)',
                boxShadow: 'inset 0 1px 3px rgba(37, 99, 235, 0.06), 0 1px 2px rgba(255, 255, 255, 0.8)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: 'inset 0 1px 3px rgba(37, 99, 235, 0.08), 0 2px 4px rgba(37, 99, 235, 0.06)',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.15), inset 0 1px 3px rgba(37, 99, 235, 0.05)',
                },
              },
            }}
          />
        )}
      />
    </Box>
  );

  return (
    <>
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={`${template ? 'Edit' : 'Create'} Cheque Template - ${bank?.accountName || 'Bank Account'} (${bank?.accountNumber || 'N/A'})`}
      titleIcon={<TemplateIcon />}
      variant="form"
      maxWidth="xl"
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          width: '95vw !important',
          maxWidth: '1400px !important',
          minWidth: '900px !important',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 40%, #f1f5ff 100%)',
          boxShadow: '0 8px 32px rgba(37, 99, 235, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
        }
      }}
      actions={
        <>
          <EasyCheckButton
            buttonType="form"
            variant="outlined"
            startIcon={<HelpIcon />}
            onClick={() => setHelpModalOpen(true)}
            disabled={loading}
          >
            Help
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            variant="outlined"
            startIcon={<LibraryBooksIcon />}
            onClick={() => setImportDialogOpen(true)}
            disabled={loading}
            sx={{ mr: 'auto' }}
          >
            Import from Library
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            type="submit"
            form="template-form"
            loading={loading}
          >
            {template ? 'Update Template' : 'Create Template'}
          </EasyCheckButton>
        </>
      }
    >
      <form id="template-form" onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', gap: 2.5, minHeight: 400 }}>

          {/* === LEFT PANEL: Image Upload + Preview + Auto-detect === */}
          <Box sx={{ flex: '1.2 1 0%', display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>

            {/* Field chips — click to enter placement mode for that field */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.entries(FIELD_CONFIG)
                .filter(([key]) => {
                  if (key === 'beneficiary2' && !includeBeneficiary2) return false;
                  if (key === 'remarks' && !includeRemarks) return false;
                  return true;
                })
                .map(([key, config]) => {
                const hasCoordinates = formValues[config.fields.left] !== '' && formValues[config.fields.left] !== undefined
                  && formValues[config.fields.top] !== '' && formValues[config.fields.top] !== undefined;
                const isActive = activeField === key;
                const isPlacing = placementField === key;

                return (
                  <Chip
                    key={key}
                    label={config.label}
                    size="small"
                    onClick={() => {
                      if (!designerEnabled) return;
                      if (isPlacing) {
                        // Toggle off placement mode
                        setPlacementField(null);
                      } else if (hasCoordinates) {
                        // Field already placed — select/highlight it
                        handleFieldClick(key);
                      } else {
                        // No coordinates yet — enter placement mode
                        setPlacementField(key);
                        setActiveField(key);
                      }
                    }}
                    sx={{
                      fontSize: '0.65rem',
                      height: 24,
                      fontWeight: isPlacing || isActive ? 700 : 500,
                      backgroundColor: isPlacing ? config.color : isActive ? `${config.color}25` : config.bgColor,
                      color: isPlacing ? 'white' : config.color,
                      border: `1.5px solid ${config.color}`,
                      cursor: !designerEnabled ? 'default' : 'pointer',
                      opacity: !designerEnabled ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: isPlacing ? config.color : `${config.color}30`,
                      },
                    }}
                  />
                );
              })}
              <Chip
                label="Clear All"
                size="small"
                icon={<ClearAllIcon sx={{ fontSize: 14 }} />}
                onClick={() => { if (designerEnabled) handleClearAllFields(); }}
                sx={{
                  fontSize: '0.65rem',
                  height: 24,
                  fontWeight: 500,
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  color: '#ef4444',
                  border: '1.5px solid #ef4444',
                  cursor: !designerEnabled ? 'default' : 'pointer',
                  opacity: !designerEnabled ? 0.5 : 1,
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.18)',
                  },
                }}
              />
            </Box>

            {/* Designer lock toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EasyCheckButton
                buttonType="form"
                variant={designerEnabled ? 'contained' : 'outlined'}
                size="small"
                startIcon={designerEnabled ? <LockOpenIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                onClick={() => setDesignerEnabled(prev => !prev)}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                  backgroundColor: designerEnabled ? '#16a34a' : undefined,
                  borderColor: designerEnabled ? '#16a34a' : '#94a3b8',
                  color: designerEnabled ? 'white' : '#64748b',
                  '&:hover': {
                    backgroundColor: designerEnabled ? '#15803d' : 'rgba(100, 116, 139, 0.08)',
                    borderColor: designerEnabled ? '#15803d' : '#64748b',
                  },
                }}
              >
                {designerEnabled ? 'Designer Enabled' : 'Enable Designer'}
              </EasyCheckButton>
              {!designerEnabled && (
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                  Upload an image or enable designer to edit field positions
                </Typography>
              )}
            </Box>

            {/* Cheque Preview — always visible with draggable overlays */}
            <Box
              sx={{ position: 'relative' }}
              onDragOver={!uploadedImage ? handleDragOver : undefined}
              onDragLeave={!uploadedImage ? handleDragLeave : undefined}
              onDrop={!uploadedImage ? handleDrop : undefined}
            >
              {isUploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    zIndex: 30,
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}
              <ChequeImagePreview
                designerEnabled={designerEnabled}
                imageUrl={imagePreviewUrl || null}
                templateWidth={formValues.templateWidth}
                templateHeight={formValues.templateHeight}
                formValues={formValues}
                activeField={activeField}
                onFieldClick={handleFieldClick}
                onFieldMove={handleFieldMove}
                onFieldResize={handleFieldResize}
                placementField={placementField}
                onPlaceField={handlePlaceField}
                hiddenFields={[
                  ...(!includeBeneficiary2 ? ['beneficiary2'] : []),
                  ...(!includeRemarks ? ['remarks'] : []),
                ]}
              />
              {uploadedImage && (
                <EasyCheckButton
                  buttonType="icon"
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </EasyCheckButton>
              )}
              {!uploadedImage && isDragOver && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    zIndex: 25,
                  }}
                >
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    Drop cheque image here
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Upload / Replace Image button */}
            <Box sx={{ textAlign: 'center' }}>
              <EasyCheckButton
                buttonType="form"
                variant={uploadedImage ? 'text' : 'outlined'}
                size="small"
                startIcon={uploadedImage ? <ImageIcon /> : <UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {uploadedImage ? 'Replace Image' : 'Upload Cheque Image'}
              </EasyCheckButton>
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/tiff"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
              />
            </Box>

            {/* Auto-detect button */}
            <EasyCheckButton
              buttonType="form"
              variant="outlined"
              startIcon={isDetecting ? <CircularProgress size={16} /> : <DetectIcon />}
              onClick={handleDetectFields}
              disabled={!uploadedImage || isDetecting || isUploading}
              fullWidth
              sx={{ py: 1 }}
            >
              {isDetecting ? 'Detecting Fields...' : 'Auto-detect Fields'}
            </EasyCheckButton>

            {isDetecting && <LinearProgress sx={{ borderRadius: 1 }} />}

            {/* Status message */}
            {detectMessage && (
              <Alert severity={detectMessage.type} sx={{ py: 0.5, fontSize: '0.8rem' }}>
                {detectMessage.text}
              </Alert>
            )}

            {/* Image info */}
            {uploadedImage && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                {uploadedImage.PixelWidth > 0
                  ? `${uploadedImage.PixelWidth}x${uploadedImage.PixelHeight}px | ${uploadedImage.PhysicalWidthCm}x${uploadedImage.PhysicalHeightCm}cm`
                  : uploadedImage.FileName}
              </Typography>
            )}

          </Box>

          {/* === RIGHT PANEL: Coordinate Fields (scrollable) === */}
          <Box
            sx={{
              flex: '1 1 0%',
              overflowY: 'auto',
              maxHeight: 'calc(90vh - 180px)',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                borderRadius: 3,
              },
            }}
          >
            <Stack spacing={1.25}>

              {/* Template Size */}
              <SectionPanel
                title="Template Size (cm)"
                icon={<SizeIcon sx={{ fontSize: 14 }} />}
                gradient="default"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('templateWidth', 'Width', dimensionRules)}
                  {renderField('templateHeight', 'Height', dimensionRules)}
                </Box>
              </SectionPanel>

              {/* Beneficiary */}
              <SectionPanel
                title="Beneficiary"
                icon={<BeneficiaryIcon sx={{ fontSize: 14 }} />}
                gradient="default"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('beneficiaryLeft', 'Left')}
                  {renderField('beneficiaryTop', 'Top')}
                  {renderField('beneficiaryWidth', 'Width', dimensionRules)}
                </Box>
              </SectionPanel>

              {/* Beneficiary Line 2 (only shown when enabled in company preferences) */}
              {includeBeneficiary2 && (
              <SectionPanel
                title="Beneficiary Line 2"
                icon={<BeneficiaryIcon sx={{ fontSize: 14 }} />}
                gradient="alt"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('beneficiary2Left', 'Left')}
                  {renderField('beneficiary2Top', 'Top')}
                </Box>
              </SectionPanel>
              )}

              {/* Date */}
              <SectionPanel
                title="Date"
                icon={<DateIcon sx={{ fontSize: 14 }} />}
                gradient="alt"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('dateLeft', 'Left')}
                  {renderField('dateTop', 'Top')}
                </Box>
              </SectionPanel>

              {/* Amount */}
              <SectionPanel
                title="Amount"
                icon={<AmountIcon sx={{ fontSize: 14 }} />}
                gradient="default"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('amountLeft', 'Left')}
                  {renderField('amountTop', 'Top')}
                </Box>
              </SectionPanel>

              {/* Amount in Words - Line 1 */}
              <SectionPanel
                title="Amount in Words - Line 1"
                icon={<WordsIcon sx={{ fontSize: 14 }} />}
                gradient="accent"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('amountInWords1Left', 'Left')}
                  {renderField('amountInWords1Top', 'Top')}
                  {renderField('amountInWordsWidth', 'Width')}
                </Box>
              </SectionPanel>

              {/* Amount in Words - Line 2 */}
              <SectionPanel
                title="Amount in Words - Line 2"
                icon={<WordsIcon sx={{ fontSize: 14 }} />}
                gradient="accent"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('amountInWords2Left', 'Left')}
                  {renderField('amountInWords2Top', 'Top')}
                  {renderField('amount2Width', 'Width')}
                </Box>
              </SectionPanel>

              {/* A/C Payee */}
              <SectionPanel
                title="A/C Payee"
                icon={<PayeeIcon sx={{ fontSize: 14 }} />}
                gradient="alt"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('accountPayeeLeft', 'Left')}
                  {renderField('accountPayeeTop', 'Top')}
                </Box>
              </SectionPanel>

              {/* Remarks (only shown when enabled in company preferences) */}
              {includeRemarks && (
              <SectionPanel
                title="Remarks"
                icon={<RemarksIcon sx={{ fontSize: 14 }} />}
                gradient="accent"
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {renderField('remarksLeft', 'Left')}
                  {renderField('remarksTop', 'Top')}
                </Box>
              </SectionPanel>
              )}

            </Stack>
          </Box>

        </Box>
      </form>
    </EasyCheckDialog>

    {/* DPI Input Dialog */}
    <DpiInputDialog
      open={showDpiDialog}
      onClose={() => {
        setShowDpiDialog(false);
        setPendingUploadData(null);
      }}
      onConfirm={handleDpiConfirm}
      pixelWidth={pendingUploadData?.PixelWidth || 0}
      pixelHeight={pendingUploadData?.PixelHeight || 0}
      initialWidth={pendingUploadData?.PhysicalWidthCm || null}
      initialHeight={pendingUploadData?.PhysicalHeightCm || null}
      hasDpiMetadata={pendingUploadData?.HasDpiMetadata ?? pendingUploadData?.hasDpiMetadata ?? false}
    />

    {/* Help Modal */}
    <TemplateHelpModal
      open={helpModalOpen}
      onClose={() => setHelpModalOpen(false)}
    />
    <TemplateImportDialog
      open={importDialogOpen}
      onClose={() => setImportDialogOpen(false)}
      onImport={handleLibraryImport}
    />
    </>
  );
};

export default TemplateFormModal;
